/**
 * FC Product Card Slider + Mid-section Slider (Swiper-based)
 *
 * Gallery mode: arrows scroll through product images, dots = image count.
 * Swatch sync: watches the `swatch-generated` attribute that variant-swatch-king
 * sets on .fc-product-card when a swatch is clicked. Fetches the new product's
 * full image gallery via AJAX and rebuilds the swiper.
 */
(function () {
  'use strict';

  function getSwiperClass() {
    return window.FoxTheme && FoxTheme.Swiper && FoxTheme.Swiper.Swiper;
  }

  /* --- Product image fetch cache --- */
  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) {
      return Promise.resolve(productCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        productCache[handle] = data;
        return data;
      });
  }

  /* --- Build slide HTML from a Shopify image URL --- */
  function buildSlideHTML(imageUrl, alt, sizes) {
    var base = imageUrl.split('?')[0];
    var widths = [70, 140, 165, 355, 450, 535, 710, 900, 1070, 1420];
    var srcset = widths.map(function (w) {
      return base + '?width=' + w + ' ' + w + 'w';
    }).join(', ');

    return '<div class="swiper-slide">' +
      '<div class="media-wrapper product-card__image product-card__image--main">' +
      '<img src="' + base + '?width=710"' +
      ' srcset="' + srcset + '"' +
      ' sizes="' + sizes + '"' +
      ' alt="' + (alt || '').replace(/"/g, '&quot;') + '"' +
      ' loading="lazy" class="motion-reduce"' +
      ' width="710" height="auto">' +
      '</div></div>';
  }

  /* --- Sliding-window dot pagination (max 5 visible) --- */
  var FC_MAX_DOTS = 5;

  function updatePaginationWindow(paginationEl, realIndex) {
    if (!paginationEl) return;
    var bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
    var total = bullets.length;
    /* Guard: realIndex may be undefined during early Swiper init */
    if (typeof realIndex !== 'number' || isNaN(realIndex)) realIndex = 0;

    if (total <= FC_MAX_DOTS) {
      /* Show all dots normally when ≤ 5 bullets */
      for (var i = 0; i < total; i++) {
        bullets[i].style.display = '';
        bullets[i].style.transform = '';
      }
      paginationEl.classList.remove('fc-pagination--windowed');
      return;
    }

    paginationEl.classList.add('fc-pagination--windowed');

    /* Calculate the visible window of FC_MAX_DOTS centered on the active dot */
    var half = Math.floor(FC_MAX_DOTS / 2);
    var start = Math.max(0, Math.min(realIndex - half, total - FC_MAX_DOTS));
    var end = start + FC_MAX_DOTS;

    for (var j = 0; j < total; j++) {
      if (j >= start && j < end) {
        bullets[j].style.display = '';
        /* Scale edge dots for a smooth scroll-like feel */
        if (j === start && start > 0) {
          bullets[j].style.transform = 'scale(0.6)';
        } else if (j === end - 1 && end < total) {
          bullets[j].style.transform = 'scale(0.6)';
        } else {
          bullets[j].style.transform = 'scale(1)';
        }
      } else {
        bullets[j].style.display = 'none';
        bullets[j].style.transform = '';
      }
    }
  }

  /* --- Init or reinit swiper on an element --- */
  function createSwiper(el, SwiperClass) {
    if (el._fcSwiper) {
      el._fcSwiper.destroy(true, true);
      el._fcSwiper = null;
    }

    var prevBtn = el.querySelector('[data-fc-pcard-prev]');
    var nextBtn = el.querySelector('[data-fc-pcard-next]');
    var paginationEl = el.querySelector('[data-fc-pcard-pagination]');
    var slideCount = el.querySelectorAll('.swiper-slide').length;

    /* Prevent arrow clicks from bubbling to the parent <a> tag */
    [prevBtn, nextBtn].forEach(function (btn) {
      if (btn && !btn._fcClickGuard) {
        btn._fcClickGuard = true;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
        });
      }
    });

    var swiper = new SwiperClass(el, {
      modules: [
        FoxTheme.Swiper.Navigation,
        FoxTheme.Swiper.Pagination,
        FoxTheme.Swiper.EffectFade,
      ],
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 1,
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      navigation: { prevEl: prevBtn, nextEl: nextBtn },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      on: {
        init: function () {
          updatePaginationWindow(paginationEl, this.realIndex);
        },
        slideChange: function () {
          updatePaginationWindow(paginationEl, this.realIndex);
        },
        paginationUpdate: function () {
          updatePaginationWindow(paginationEl, this.realIndex);
        },
      },
    });

    el._fcSwiper = swiper;

    return swiper;
  }

  /* --- Rebuild the gallery with a new product's images --- */
  function rebuildGallery(el, handle, SwiperClass) {
    var sizes = el.dataset.fcImageSizes || '450px';

    /* Show loading shimmer */
    el.classList.add('fc-pcard-swiper--loading');

    fetchProduct(handle).then(function (product) {
      if (!product || !product.images || !product.images.length) {
        el.classList.remove('fc-pcard-swiper--loading');
        return;
      }

      var wrapper = el.querySelector('.swiper-wrapper');
      if (!wrapper) {
        el.classList.remove('fc-pcard-swiper--loading');
        return;
      }

      var html = '';
      for (var i = 0; i < product.images.length; i++) {
        var img = product.images[i];
        var src = typeof img === 'string' ? img : img.src;
        var alt = typeof img === 'string' ? (product.title || '') : (img.alt || product.title || '');
        html += buildSlideHTML(src, alt, sizes);
      }
      wrapper.innerHTML = html;

      /* Wait for first image to load before revealing */
      var firstImg = wrapper.querySelector('img');
      if (firstImg && !firstImg.complete) {
        firstImg.addEventListener('load', function () {
          el.classList.remove('fc-pcard-swiper--loading');
        }, { once: true });
        firstImg.addEventListener('error', function () {
          el.classList.remove('fc-pcard-swiper--loading');
        }, { once: true });
      } else {
        el.classList.remove('fc-pcard-swiper--loading');
      }

      createSwiper(el, SwiperClass);
      el.dataset.fcProductHandle = handle;
    });
  }

  /* --- Product Card Slider --- */
  function initPcardSlider(el) {
    if (el._fcSwiperInit) return;
    var SwiperClass = getSwiperClass();
    if (!SwiperClass) return;
    el._fcSwiperInit = true;

    createSwiper(el, SwiperClass);

    var card = el.closest('.fc-product-card');
    if (!card) return;

    /*
     * Watch for `swatch-generated` attribute changes on .fc-product-card.
     * The variant-swatch-king app sets this attribute to the new product handle
     * whenever a swatch is clicked. This is more reliable than click listeners
     * because the app may stop propagation or use capture-phase handlers.
     */
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'swatch-generated') {
          var newHandle = card.getAttribute('swatch-generated');
          if (newHandle && newHandle !== el.dataset.fcProductHandle) {
            rebuildGallery(el, newHandle, SwiperClass);
          }
          break;
        }
      }
    });

    observer.observe(card, { attributes: true, attributeFilter: ['swatch-generated'] });
  }

  /* --- Mid-section Slider --- */
  function initMidSlider(el) {
    if (el.dataset.fcMidInit) return;
    if (!FoxTheme || !FoxTheme.Carousel) return;
    el.dataset.fcMidInit = 'true';

    var modules = [FoxTheme.Swiper.Navigation];

    var carousel = new FoxTheme.Carousel(
      el,
      {
        slidesPerView: "auto",
        spaceBetween: 8,
        watchOverflow: true,
        centeredSlides: true,
        loop: true,
        initialSlide: 1,
        navigation: {
          prevEl: el.querySelector("[data-fc-mid-prev]"),
          nextEl: el.querySelector("[data-fc-mid-next]"),
        },
        breakpoints: {
          1024: { spaceBetween: 16 },
        },
      },
      modules,
    );

    carousel.init();
  }

  /* --- Init all sliders --- */
  function initAll(container) {
    var root = container || document;

    if (!getSwiperClass()) {
      setTimeout(function () { initAll(container); }, 100);
      return;
    }
    console.log(root);

    root.querySelectorAll('[data-fc-pcard-swiper]').forEach(initPcardSlider);

    var mid = document.querySelector('[data-fc-mid-slider]');
    if (mid) initMidSlider(mid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    console.log('Document ready, initializing sliders');
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    console.log('Section loaded:', e.target);
    initAll(e.target);
  });
})();
